import { checkErrors, getDefaultOptions } from "../services/Category";
import { simpleQuery } from "../services/utils";
import {
  LocationCandidate,
  PropertyLocation,
  PropertyMapLocation,
} from "./types";

const locationUrl = (propertyId: number): string =>
  new URL(
    `/api/hebergements/${propertyId}/location/`,
    window.origin
  ).toString();

// Search by name or address, restricted server-side to hotels/campings and
// biased to the given countries (ISO-2 codes).
export const searchLocations = async (
  query: string,
  countryCodes: string[]
): Promise<LocationCandidate[]> => {
  const url = new URL("/api/geocoding/search/", window.origin);
  url.searchParams.set("q", query);
  if (countryCodes.length > 0) {
    url.searchParams.set("country", countryCodes.join(","));
  }
  return simpleQuery(url.toString());
};

// Every property the user can see, with coordinates when geocoded. Powers
// the reporting map (points + geocoding-coverage banner) in one call.
export const fetchPropertyMapLocations = async (): Promise<
  PropertyMapLocation[]
> => {
  return simpleQuery(
    new URL("/api/geocoding/locations/", window.origin).toString()
  );
};

export const getPropertyLocation = async (
  propertyId: number
): Promise<PropertyLocation | null> => {
  const response = await fetch(locationUrl(propertyId), getDefaultOptions());
  checkErrors(response);
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const savePropertyLocation = async (
  propertyId: number,
  feature: Record<string, unknown>
): Promise<PropertyLocation> => {
  const response = await fetch(
    locationUrl(propertyId),
    getDefaultOptions({ method: "PUT", body: JSON.stringify({ feature }) })
  );
  checkErrors(response);
  return response.json();
};

export const clearPropertyLocation = async (
  propertyId: number
): Promise<void> => {
  const response = await fetch(
    locationUrl(propertyId),
    getDefaultOptions({ method: "DELETE" })
  );
  checkErrors(response);
};

// Fetched as an authenticated blob (an <img> tag can't send our auth headers);
// the API key stays server-side behind this proxy.
export const fetchStaticMap = async (
  lat: number,
  lon: number
): Promise<Blob> => {
  const url = new URL("/api/geocoding/staticmap/", window.origin);
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lon));
  // Request a larger image than the display box so it stays sharp under cover.
  url.searchParams.set("width", "640");
  url.searchParams.set("height", "440");
  const response = await fetch(url.toString(), getDefaultOptions());
  checkErrors(response);
  return response.blob();
};
